/**
 * Utility functions for PIN session management
 * Provides centralized access to PIN session data stored in localStorage
 */

export interface PinSessionData {
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    /** Optional preferred first name shown on team-facing kiosk surfaces
     *  (greetings, clock in/out confirmations). Falls back to first_name.
     *  Does NOT replace legal first_name on payroll or HR documents. */
    preferred_first_name?: string | null;
    role: string;
    company_id: string;
    user_id?: string;
    login_count?: number;
  };
  company_code: string;
  location_id?: string;
  timestamp: number;
  company?: {
    id: string;
    name: string;
    code: string;
    logo_url?: string;
  };
  location?: {
    id: string;
    name: string;
    code: string;
  };
  store_location?: string;
  session_token?: string;
  /** Per-location DashAlign kiosk time-clock switch, resolved server-side by
   *  validate-pin-session. Gates the Time Clock nav item + /limited/time-clock
   *  route. Default false everywhere except stores explicitly opted in. */
  kiosk_clock_enabled?: boolean;
  /** Per-location DashAlign kiosk schedule switch, resolved server-side by
   *  validate-pin-session. Gates the Schedule nav item + /limited/schedule
   *  route. Default false everywhere except stores explicitly opted in. */
  kiosk_schedule_enabled?: boolean;
}

/**
 * Check if the current session is a PIN session.
 * Route-aware: only returns true on /limited routes to prevent
 * leftover pin_session data from affecting admin/auth views.
 */
export function isPinSession(): boolean {
  try {
    if (!window.location.pathname.startsWith('/limited')) return false;
    const session = localStorage.getItem('pin_session');
    return session !== null;
  } catch {
    return false;
  }
}

/**
 * Get the current PIN session data.
 * Route-aware: only returns data on /limited routes.
 * Returns null if no PIN session exists, not on a limited route, or if parsing fails.
 */
export function getPinSessionData(): PinSessionData | null {
  try {
    if (!window.location.pathname.startsWith('/limited')) return null;
    const session = localStorage.getItem('pin_session');
    if (!session) return null;
    return JSON.parse(session);
  } catch (error) {
    console.error('Failed to parse PIN session data:', error);
    return null;
  }
}

/**
 * Get company information from PIN session
 * Returns null if no session or no company data
 */
export function getPinSessionCompany() {
  const session = getPinSessionData();
  return session?.company || null;
}

/**
 * Get location information from PIN session
 * Returns null if no session or no location data
 */
export function getPinSessionLocation() {
  const session = getPinSessionData();
  return session?.location || null;
}

/**
 * Get profile information from PIN session
 * Returns null if no session
 */
export function getPinSessionProfile() {
  const session = getPinSessionData();
  return session?.profile || null;
}

/**
 * Get company code from PIN session
 * Returns null if no session
 */
export function getPinSessionCompanyCode(): string | null {
  const session = getPinSessionData();
  return session?.company_code || null;
}

/**
 * Check if user should skip direct Supabase queries
 * PIN sessions cannot use the global unauthenticated Supabase client
 */
export function shouldSkipDirectQuery(): boolean {
  return isPinSession();
}

/**
 * Log a warning if a direct query is attempted during PIN session
 */
export function warnDirectQueryInPinSession(context: string) {
  if (isPinSession()) {
    console.warn(`⚠️ [PIN Session Warning] Direct Supabase query attempted in PIN session: ${context}`);
    console.warn('PIN sessions should use pre-loaded session data or edge functions instead.');
  }
}

/**
 * Brute-force remove any Supabase `sb-*-auth-token` keys from localStorage.
 *
 * This is a belt-and-suspenders safety net for teardown paths (PIN auto-logout,
 * manual PIN logout, kiosk teardown). `supabase.auth.signOut({ scope: 'local' })`
 * is the canonical way to clear the session, but in practice we've seen it
 * fail silently on kiosk devices — offline, CORS, or a hung refresh-token
 * request can leave the token in localStorage. When that happens the next
 * page load hydrates the previous admin/email session, and the pre-login
 * /limited view (which needs `!profile` to take the read-only kiosk path)
 * sees a stale profile and hangs on "Loading communications…".
 *
 * Safe to call even when there is no session — it is a no-op if no matching
 * keys exist. Callers should still call signOut() first (race it against a
 * short timeout) so the Supabase client emits the SIGNED_OUT event cleanly;
 * this helper just makes sure storage is actually empty afterward.
 */
export function clearSupabaseAuthTokens() {
  try {
    // Iterate backwards because removeItem mutates the indices.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage access can throw in private-mode / quota-exceeded edge
    // cases; nothing we can do, just don't let it propagate.
  }
}
